import { createStyles, Group, UnstyledButton } from '@mantine/core';
import { Icon } from '@repo/foundations';
import { type ForwardedRef, forwardRef } from 'react';
import { FilterOperator } from '../types';
import type { FilterNestedTargetMenuProps } from './FilterNestedTargetMenu';
import { FilterNestedTargetMenu } from './FilterNestedTargetMenu';
import { FilterTargetLabel } from './FilterTargetLabel';
import type { OperatorMenuProps } from './OperatorMenu';
import { OperatorMenu } from './OperatorMenu';

const useStyles = createStyles(
	(theme, { hasClear }: { hasClear: boolean }) => ({
		root: {
			border: `1px solid ${theme.other.getColor('border/primary/default')}`,
			backgroundColor: theme.other.getColor('fill/primary/default'),
			borderRadius: theme.radius.sm,
			padding: `0 0 0 ${theme.spacing.sm}`,
			gap: 0,

			button: {
				outline: 'none !important',
			},
			minWidth: 0,
		},
		detailedRoot: {
			gap: 0,
			minWidth: 0,
		},
		detailedLabel: {
			paddingRight: theme.spacing['2xs'],
		},
		operatorMenu: {
			padding: `${theme.spacing['5xs']} ${theme.spacing['2xs']}`,
			lineHeight: theme.spacing.lg,
		},
		target: {
			backgroundColor: theme.other.getColor('fill/primary/default'),
			padding: `${theme.spacing['5xs']} ${theme.spacing['2xs']}`,
			borderTopRightRadius: !hasClear ? theme.radius.sm : undefined,
			borderBottomRightRadius: !hasClear ? theme.radius.sm : undefined,
			minWidth: 0,
			maxWidth: '100%',

			'&:hover, &:focus': {
				backgroundColor: theme.other.getColor('fill/primary/hover'),
			},

			'&:active': {
				backgroundColor: theme.other.getColor('fill/primary/active'),
			},
		},
		detailedValue: {
			fontSize: theme.fontSizes.xs,
			color: theme.other.getColor('text/primary/default'),
			lineHeight: theme.spacing.lg,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
		clearButton: {
			padding: `${theme.spacing['5xs']} ${theme.spacing.xs} ${theme.spacing['5xs']} 0`,
			borderTopRightRadius: theme.radius.sm,
			borderBottomRightRadius: theme.radius.sm,
			backgroundColor: theme.other.getColor('fill/primary/default'),
			color: theme.other.getColor('icon/primary/default'),

			'&:hover, &:focus': {
				backgroundColor: theme.other.getColor('fill/primary/hover'),
				color: theme.other.getColor('icon/primary/hover'),
			},

			'&:active': {
				backgroundColor: theme.other.getColor('fill/primary/active'),
				color: theme.other.getColor('icon/primary/active'),
			},
		},
	})
);

export interface FilterTargetDetailedProps {
	label: string;
	valueLabel: React.ReactNode | null;
	isMenuOpen?: boolean;
	onClear?: () => void;
	onToggle: () => void;
	operatorMenuProps: Omit<OperatorMenuProps, 'className'>;
	nestedMenuProps?: Omit<FilterNestedTargetMenuProps, 'className'> | null;
	isNestedList?: boolean;
}

export const FilterTargetDetailed = forwardRef(
	(
		{
			valueLabel,
			isMenuOpen,
			label,
			onClear,
			onToggle,
			operatorMenuProps,
			isNestedList = false,
			nestedMenuProps,
		}: FilterTargetDetailedProps,
		ref: ForwardedRef<HTMLButtonElement>
	) => {
		const { classes } = useStyles({
			hasClear: !!onClear,
		});

		const canShowTarget = operatorMenuProps?.selected
			? ![FilterOperator.isSet, FilterOperator.isNotSet].includes(
					operatorMenuProps.selected
				)
			: true;

		const canShowNestedTarget =
			operatorMenuProps?.selected === FilterOperator.Contains && isNestedList;

		return (
			<Group
				data-testid="filter-target-detailed"
				className={classes.root}
				noWrap
			>
				<Group className={classes.detailedRoot} noWrap>
					<FilterTargetLabel
						label={label}
						showChevron={false}
						className={classes.detailedLabel}
					/>
					<OperatorMenu
						{...operatorMenuProps}
						className={classes.operatorMenu}
					/>
					{canShowTarget && (
						<UnstyledButton
							ref={ref}
							onClick={onToggle}
							className={classes.target}
						>
							<Group spacing="2xs">
								<div className={classes.detailedValue}>
									{valueLabel ?? '(no selection)'}
								</div>
							</Group>
						</UnstyledButton>
					)}
					{canShowNestedTarget && canShowTarget && nestedMenuProps && (
						<FilterNestedTargetMenu {...nestedMenuProps} />
					)}
					{!onClear && (
						<Icon
							name={isMenuOpen ? 'chevronUp' : 'chevronDown'}
							iconPadding={0}
						/>
					)}
				</Group>
				{!!onClear && (
					<UnstyledButton
						onClick={onClear}
						className={classes.clearButton}
						data-testid="filter-target-remove"
					>
						<Icon name="x" />
					</UnstyledButton>
				)}
			</Group>
		);
	}
);
FilterTargetDetailed.displayName = 'FilterTargetDetailed';
